import React, { useState, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import ChatInterface from './components/ChatInterface';
import ApplicantPortal from './components/ApplicantPortal';
import UnderwriterPortal from './components/UnderwriterPortal';
import ProviderPortal from './components/ProviderPortal';
import './App.css';

const POOL_ID = process.env.REACT_APP_COGNITO_USER_POOL_ID || '';
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || '';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pendingMsg, setPendingMsg] = useState(null);
  const [pendingApplicantId, setPendingApplicantId] = useState(null);
  const [userRole, setUserRole] = useState('applicant');
  const [portalKey, setPortalKey] = useState(0);
  const authInitialized = useRef(false);

  const handleApplicationSuccess = (applicantId) => {
    setPendingMsg(`Please analyze my newly submitted application: ${applicantId}`);
    navigate('/chat');
  };

  const handleUnderwriterAsk = (applicantId) => {
    setPendingApplicantId(applicantId);
    setPendingMsg(`Perform a comprehensive risk assessment and medical analysis for applicant ${applicantId}. Provide an underwriting recommendation.`);
    navigate('/chat');
  };

  const handleSignOut = useCallback(() => {
    if (POOL_ID && CLIENT_ID) {
      const pool = new CognitoUserPool({ UserPoolId: POOL_ID, ClientId: CLIENT_ID });
      const currentUser = pool.getCurrentUser();
      if (currentUser) currentUser.signOut();
    }

    localStorage.removeItem('iu_login_at');
    authInitialized.current = false;
    setIsAuthenticated(false);
    setPendingMsg(null);
    setPendingApplicantId(null);
    setUserRole('applicant');
    setPortalKey(current => current + 1);
    navigate('/');
  }, [navigate]);

  const handleAuthChange = useCallback((user) => {
    const signedIn = Boolean(user && user.email);
    setIsAuthenticated(signedIn);

    if (!signedIn) {
      setPendingMsg(null);
      setUserRole('applicant');
      authInitialized.current = false;
      navigate('/');
      return;
    }

    const groups = user.groups || [];
    if (groups.includes('underwriter')) {
      setUserRole('underwriter');
      if (!authInitialized.current) {
        authInitialized.current = true;
        navigate('/underwriter');
      }
      return;
    }

    if (groups.includes('provider')) {
      setUserRole('provider');
      if (!authInitialized.current) {
        authInitialized.current = true;
        navigate('/provider');
      }
      return;
    }

    setUserRole('applicant');
    if (!authInitialized.current) {
      authInitialized.current = true;
      navigate('/applications');
    }
  }, [navigate]);

  const currentPath = location.pathname;
  const isChatRoute = currentPath === '/chat';
  const roleLabel = userRole === 'underwriter' ? 'Operations' : userRole === 'provider' ? 'Provider' : 'Applicant';
  const contextLabel = !isAuthenticated
    ? 'Secure digital intake'
    : isChatRoute
      ? 'AI decision support'
      : userRole === 'underwriter'
        ? 'Risk review workspace'
        : 'Coverage application workspace';

  return (
    <div className="app">
      <nav className="app-nav">
        <div className="app-brand">
          <div className="app-brand-mark" aria-hidden="true">
            <svg viewBox="0 0 48 48" fill="none" width="40" height="40">
              <rect width="48" height="48" rx="14" fill="url(#brandGradient)" />
              <path d="M11 31h5.5l3-11 4 15 4-18 3 14H37" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              <defs>
                <linearGradient id="brandGradient" x1="4" y1="4" x2="44" y2="44" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#A220A1" />
                  <stop offset="1" stopColor="#AB1DFE" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          <div className="app-brand-text">
            <span className="app-brand-name">Nebulane Risk Desk</span>
            <span className="app-brand-sub">Nebulane underwriting workspace</span>
          </div>
        </div>

        <div className="app-nav-right">
          <div className="app-context">
            <span className="app-context-role">{roleLabel}</span>
            <span className="app-context-copy">{contextLabel}</span>
          </div>

          {isAuthenticated && (
            <>
              <div className="app-tabs">
                {userRole === 'applicant' && (
                  <button
                    className={`app-tab ${currentPath === '/applications' ? 'active' : ''}`}
                    onClick={() => navigate('/applications')}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                    <span>Applications</span>
                  </button>
                )}

                {userRole === 'underwriter' && (
                  <button
                    className={`app-tab ${currentPath === '/underwriter' ? 'active' : ''}`}
                    onClick={() => navigate('/underwriter')}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 11l3 3L22 4" />
                      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                    </svg>
                    <span>Reviews</span>
                  </button>
                )}

                {userRole === 'provider' && (
                  <button
                    className={`app-tab ${currentPath === '/provider' ? 'active' : ''}`}
                    onClick={() => navigate('/provider')}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                    </svg>
                    <span>Pre-Auth</span>
                  </button>
                )}

                <button
                  className={`app-tab ${isChatRoute ? 'active' : ''}`}
                  onClick={() => navigate('/chat')}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  <span>AI Workspace</span>
                </button>
              </div>

              <button className="app-signout" onClick={handleSignOut} title="Sign out">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span>Sign Out</span>
              </button>
            </>
          )}
        </div>
      </nav>

      <main className={`app-main ${isChatRoute ? 'chat-mode' : 'scrollable'}`}>
        <div className={`app-stage ${isChatRoute ? 'chat-stage' : ''}`}>
          <Routes>
            <Route
              path="/"
              element={(
                <ApplicantPortal
                  key={portalKey}
                  onSwitchToChat={handleApplicationSuccess}
                  onAuthChange={handleAuthChange}
                />
              )}
            />
            <Route
              path="/applications"
              element={isAuthenticated ? (
                <ApplicantPortal
                  key={portalKey}
                  onSwitchToChat={handleApplicationSuccess}
                  onAuthChange={handleAuthChange}
                />
              ) : (
                <Navigate to="/" replace />
              )}
            />
            <Route
              path="/provider"
              element={isAuthenticated && userRole === 'provider' ? (
                <ProviderPortal onAuthChange={handleAuthChange} />
              ) : (
                <Navigate to="/" replace />
              )}
            />
            <Route
              path="/underwriter"
              element={isAuthenticated && userRole === 'underwriter' ? (
                <UnderwriterPortal onSwitchToChat={handleUnderwriterAsk} />
              ) : (
                <Navigate to="/" replace />
              )}
            />
            <Route
              path="/chat"
              element={isAuthenticated ? (
                <ChatInterface
                  pendingMessage={pendingMsg}
                  onPendingMessageConsumed={() => { setPendingMsg(null); setPendingApplicantId(null); }}
                  userRole={userRole}
                  contextApplicantId={pendingApplicantId}
                />
              ) : (
                <Navigate to="/" replace />
              )}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
